import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Badge, Button, ButtonGroup, Col, Form, Modal, Row } from 'react-bootstrap'
import InputMask from 'react-input-mask'
import { components } from 'react-select'
import CreatableSelect from 'react-select/creatable'

import { FieldArray, Formik } from 'formik'
import * as yup from 'yup'

import ProviderService from '../services/ProviderService'
import { formatPhone } from '../utils/PhoneUtils'

import SmartAddressInput from './common/SmartAddressInput'
import ConfirmationModal from './controls/ConfirmationModal'
import ReclassifyProviderModal from './ReclassifyProviderModal'

const CustomOption = props => {
  const { isSelected, innerRef } = props
  const ref = useRef(null)

  useEffect(() => {
    if (isSelected && ref.current) {
      const el = ref.current
      requestAnimationFrame(() => {
        if (!ref.current) return
        const parent = el.closest('.react-select__menu-list') || el.parentElement
        if (parent) {
          const elTop = el.offsetTop
          const elHeight = el.offsetHeight
          const parentHeight = parent.clientHeight
          parent.scrollTop = Math.max(0, elTop - parentHeight / 2 + elHeight / 2)
        }
      })
    }
  }, [isSelected])

  return (
    <components.Option
      {...props}
      innerRef={node => {
        ref.current = node
        if (typeof innerRef === 'function') innerRef(node)
        else if (innerRef) innerRef.current = node
      }}
    />
  )
}

const CustomMenuList = props => {
  const listRef = useRef(null)

  useEffect(() => {
    if (listRef.current) {
      const selectedEl = listRef.current.querySelector(
        '.react-select__option--is-selected, [class*="option--is-selected"]',
      )
      if (selectedEl) {
        requestAnimationFrame(() => {
          if (listRef.current && selectedEl) {
            const list = listRef.current
            const elTop = selectedEl.offsetTop
            const elHeight = selectedEl.offsetHeight
            const listHeight = list.clientHeight
            list.scrollTop = Math.max(0, elTop - listHeight / 2 + elHeight / 2)
          }
        })
      }
    }
  }, [])

  return (
    <components.MenuList
      {...props}
      innerRef={el => {
        listRef.current = el
        if (props.innerRef) {
          if (typeof props.innerRef === 'function') props.innerRef(el)
          else props.innerRef.current = el
        }
      }}
    />
  )
}

const schema = yup.object().shape({
  classification: yup.string().nullable(),
  name: yup.string().nullable(),
  type: yup.string().nullable(),
  isSoleProprietor: yup.boolean(),
  organizationName: yup
    .string()
    .nullable()
    .when('classification', {
      is: classification => classification !== 'Individual',
      then: yup.string().nullable().required('Organization Name is required'),
    }),
  npi: yup.string().nullable().max(10, 'NPI must be at most 10 characters'),
  firstName: yup.string().nullable(),
  lastName: yup.string().nullable(),
  suffix: yup.string().nullable(),
  specialties: yup.string().nullable(),
  addresses: yup.array().of(
    yup.object().shape({
      type: yup.string().required(),
      line1: yup.string(),
      city: yup.string(),
      state: yup.string(),
      zip: yup.string(),
    }),
  ),
  individuals: yup.array().of(
    yup.object().shape({
      firstName: yup.string(),
      lastName: yup.string(),
      title: yup.string().nullable(),
      npi: yup.string().nullable().max(10),
    }),
  ),
  affiliations: yup.array().of(
    yup.object().shape({
      organizationName: yup.string().nullable(),
      role: yup.string().nullable(),
      isPrimary: yup.boolean().nullable(),
    }),
  ),
  staffContacts: yup.array().of(
    yup.object().shape({
      name: yup.string().nullable(),
      role: yup.string().nullable(),
      phone: yup.string().nullable(),
      extension: yup.string().nullable(),
      email: yup.string().nullable(),
      directAddress: yup.string().nullable(),
      notes: yup.string().nullable(),
      isPrimary: yup.boolean().nullable(),
    }),
  ),
})

const defaultInitialValues = {
  name: '',
  type: 'Treatment',
  classification: 'Organization',
  firstName: '',
  lastName: '',
  suffix: '',
  specialties: '',
  email: '',
  phone: '',
  fax: '',
  website: '',
  notes: '',
  isSoleProprietor: false,
  organizationName: '',
  npi: '',
  addresses: [
    { type: 'Mailing', line1: '', city: '', state: '', zip: '' },
    { type: 'PrimaryPractice', line1: '', city: '', state: '', zip: '' },
  ],
  individuals: [{ firstName: '', lastName: '', title: '', email: '', phone: '', npi: '' }],
  affiliations: [],
  staffContacts: [],
}

const getInitialValues = () => ({
  ...defaultInitialValues,
  addresses: [
    { type: 'Mailing', line1: '', city: '', state: '', zip: '' },
    { type: 'PrimaryPractice', line1: '', city: '', state: '', zip: '' },
  ],
  individuals: [{ firstName: '', lastName: '', title: '', email: '', phone: '', npi: '' }],
  affiliations: [],
  staffContacts: [],
})

const getProviderTypeLabel = rawType => {
  const t = String(rawType || '')
    .trim()
    .toLowerCase()
  if (t.includes('pharmacy') || t.includes('pharmacies')) return 'Pharmacy'
  if (t.includes('equipment')) return 'Equipment Provider'
  if (t === 'ime' || t.startsWith('ime ')) return 'IME Provider'
  if (t.includes('transportation')) return 'Transportation Provider'
  if (t.includes('translation')) return 'Translation Provider'
  return 'Treatment Provider'
}

export default function ProviderForm({
  show,
  onHide,
  providerId,
  onSuccess,
  initialType,
  initialClassification,
  initialData,
  className,
  dialogClassName,
  backdropClassName,
  style,
}) {
  const [model, setModel] = useState(getInitialValues)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [showConfirmClose, setShowConfirmClose] = useState(false)
  const [orgOptions, setOrgOptions] = useState([])
  const [showReclassifyModal, setShowReclassifyModal] = useState(false)
  const [reclassifiedAlert, setReclassifiedAlert] = useState(null)
  const formikRef = useRef()
  const firstInputRef = useRef()
  const searchTimeoutRef = useRef(null)

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
    }
  }, [])

  const handleClose = () => {
    if (formikRef.current && formikRef.current.dirty) {
      setShowConfirmClose(true)
    } else {
      setReclassifiedAlert(null)
      onHide()
    }
  }

  const handleReclassifyConfirm = ({
    targetClassification,
    firstName,
    lastName,
    suffix,
    organizationName,
    affiliations,
  }) => {
    if (!formikRef.current) return
    formikRef.current.setFieldValue('classification', targetClassification)
    formikRef.current.setFieldValue('isSoleProprietor', false)
    formikRef.current.setFieldValue('firstName', firstName || '')
    formikRef.current.setFieldValue('lastName', lastName || '')
    formikRef.current.setFieldValue('suffix', suffix || '')
    formikRef.current.setFieldValue('organizationName', organizationName || '')
    formikRef.current.setFieldValue('affiliations', affiliations || [])
    formikRef.current.setFieldValue('individuals', [])
    formikRef.current.setFieldValue('legacyDisplayName', '')

    if (targetClassification === 'Individual') {
      const parts = [firstName, lastName].filter(Boolean).join(' ')
      formikRef.current.setFieldValue('name', suffix ? `${parts}, ${suffix}` : parts)
    } else {
      formikRef.current.setFieldValue('name', organizationName || '')
    }

    setReclassifiedAlert(
      `Provider reclassified as ${targetClassification}. Review the updated fields below and click "Save Changes" to save.`,
    )
    requestAnimationFrame(() => {
      firstInputRef.current?.focus()
    })
  }

  const loadInitialOrgs = useCallback((knownAffs = []) => {
    ProviderService.search({
      searchText: '',
      classification: 'Organization',
      page: 1,
      take: 100,
      perPage: 100,
    })
      .then(data => {
        const results = (data.results || []).map(p => {
          const orgName = (p.organization || p.organizationName || p.name || '').trim()
          const parts = [orgName, p.specialties, p.address].filter(Boolean)
          return {
            id: p.id,
            label: parts.join(' | '),
            value: orgName,
            data: p,
          }
        })

        setOrgOptions(prev => {
          const map = new Map()
          results.forEach(item => {
            const key = item.id ? String(item.id).toLowerCase() : item.value.toLowerCase()
            map.set(key, item)
          })
          knownAffs.forEach(aff => {
            const affOrgName = (aff.organizationName || '').trim()
            const affOrgId = aff.organizationId
            if (!affOrgName && !affOrgId) return
            const key = affOrgId ? String(affOrgId).toLowerCase() : affOrgName.toLowerCase()
            if (!map.has(key)) {
              const affAddr = aff.address
                ? typeof aff.address === 'string'
                  ? aff.address
                  : [aff.address.line1, aff.address.city, aff.address.state, aff.address.zip].filter(Boolean).join(', ')
                : ''
              const affLabel = [affOrgName, affAddr].filter(Boolean).join(' | ') || affOrgName
              map.set(key, {
                id: affOrgId || undefined,
                label: affLabel,
                value: affOrgName,
                data: affOrgId ? { id: affOrgId, address: aff.address } : undefined,
              })
            }
          })
          prev.forEach(item => {
            const key = item.id ? String(item.id).toLowerCase() : item.value.toLowerCase()
            if (!map.has(key)) {
              map.set(key, item)
            }
          })
          return Array.from(map.values())
        })
      })
      .catch(() => [])
  }, [])

  const handleOrgInputChange = useCallback((inputValue, { action }) => {
    if (action === 'input-change') {
      const text = (inputValue || '').trim()
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
      if (text.length >= 2) {
        searchTimeoutRef.current = setTimeout(() => {
          ProviderService.search({
            searchText: text,
            classification: 'Organization',
            page: 1,
            take: 50,
            perPage: 50,
          })
            .then(data => {
              const results = (data.results || []).map(p => {
                const orgName = (p.organization || p.organizationName || p.name || '').trim()
                const parts = [orgName, p.specialties, p.address].filter(Boolean)
                return {
                  id: p.id,
                  label: parts.join(' | '),
                  value: orgName,
                  data: p,
                }
              })
              if (results.length > 0) {
                setOrgOptions(prev => {
                  const map = new Map()
                  prev.forEach(item => {
                    const key = item.id ? String(item.id).toLowerCase() : item.value.toLowerCase()
                    map.set(key, item)
                  })
                  results.forEach(item => {
                    const key = item.id ? String(item.id).toLowerCase() : item.value.toLowerCase()
                    if (!map.has(key)) {
                      map.set(key, item)
                    }
                  })
                  return Array.from(map.values())
                })
              }
            })
            .catch(() => {})
        }, 250)
      }
    }
  }, [])

  useEffect(() => {
    if (show && providerId) {
      setIsLoading(true)
      setError(null)
      ProviderService.get(providerId)
        .then(data => {
          if (!data) return
          let loadedAffiliations = data.affiliations || data.Affiliations || []
          if (!Array.isArray(loadedAffiliations)) loadedAffiliations = []
          const rawClass = String(data.classification || '')
            .trim()
            .toLowerCase()
          const hasIndividualName = Boolean(
            (data.firstName && data.firstName.trim()) || (data.lastName && data.lastName.trim()),
          )
          const isIndividual =
            rawClass === 'individual' ||
            (rawClass !== 'organization' && (Boolean(data.isIndividual) || hasIndividualName))
          if (isIndividual && loadedAffiliations.length === 0 && (data.parentProviderId || data.organizationName)) {
            loadedAffiliations = [
              {
                organizationId: data.parentProviderId || null,
                organizationName: data.organizationName || data.parentProvider?.name || '',
                role: 'Primary Practice',
                isPrimary: true,
              },
            ]
          }

          const rawAddresses = data.addresses || data.Addresses || []
          const incomingAddresses =
            Array.isArray(rawAddresses) && rawAddresses.length > 0
              ? rawAddresses.map(a => ({ ...a }))
              : [
                  { type: 'Mailing', line1: '', city: '', state: '', zip: '' },
                  { type: 'PrimaryPractice', line1: '', city: '', state: '', zip: '' },
                ]

          const rawIndividuals = data.individuals || data.Individuals || []
          const incomingIndividuals =
            Array.isArray(rawIndividuals) && rawIndividuals.length > 0
              ? rawIndividuals.map(i => ({ ...i }))
              : [{ firstName: '', lastName: '', title: '', email: '', phone: '', npi: '' }]

          const rawOrgName = (data.organizationName || '').trim()
          const rawDisplayName = (data.name || '').trim()
          const hasLegacyHybridName =
            !isIndividual &&
            Boolean(rawOrgName) &&
            Boolean(rawDisplayName) &&
            rawOrgName.toLowerCase() !== rawDisplayName.toLowerCase()
          const canonicalOrgName = !isIndividual ? rawOrgName || rawDisplayName : data.organizationName || ''

          const rawStaffContacts = data.staffContacts || data.StaffContacts || []
          const incomingStaffContacts = Array.isArray(rawStaffContacts)
            ? rawStaffContacts.map(c => ({
                ...c,
                phone: c.phone ? formatPhone(c.phone) : '',
              }))
            : []

          const formModel = {
            ...getInitialValues(),
            ...data,
            name: isIndividual ? data.name || '' : canonicalOrgName,
            organizationName: canonicalOrgName,
            legacyDisplayName: hasLegacyHybridName ? rawDisplayName : '',
            type: data.type || initialType || 'Treatment',
            classification: isIndividual ? 'Individual' : data.classification || 'Organization',
            phone: data.phone ? formatPhone(data.phone) : '',
            fax: data.fax ? formatPhone(data.fax) : '',
            addresses: incomingAddresses,
            individuals: incomingIndividuals.map(i => ({
              ...i,
              phone: i.phone ? formatPhone(i.phone) : '',
            })),
            affiliations: loadedAffiliations,
            staffContacts: incomingStaffContacts,
          }

          if (isIndividual && !formModel.firstName && !formModel.lastName && formModel.name) {
            const parts = formModel.name.trim().split(/\s+/)
            if (parts.length >= 2) {
              const credentials = ['MD', 'DO', 'DC', 'PT', 'DPT', 'OD', 'NP', 'PA-C', 'PA', 'PhD']
              const lastPart = parts[parts.length - 1].replace(/[.,]/g, '')
              const firstPart = parts[0].replace(/[.,]/g, '')
              let parsedSuffix = ''
              let first = ''
              let last = ''
              if (credentials.some(c => c.toLowerCase() === lastPart.toLowerCase())) {
                parsedSuffix = parts[parts.length - 1]
                last = parts[parts.length - 2]
                first = parts.slice(0, parts.length - 2).join(' ')
              } else if (
                credentials.some(c => c.toLowerCase() === firstPart.toLowerCase()) ||
                firstPart.toLowerCase() === 'dr'
              ) {
                parsedSuffix = parts[0]
                first = parts[1]
                last = parts.slice(2).join(' ')
              } else {
                first = parts[0]
                last = parts.slice(1).join(' ')
              }
              if (first) formModel.firstName = first
              if (last) formModel.lastName = last
              if (parsedSuffix && !formModel.suffix) formModel.suffix = parsedSuffix
            }
          }

          if (!formModel.addresses.some(a => a.type === 'Mailing')) {
            formModel.addresses.unshift({ type: 'Mailing', line1: '', city: '', state: '', zip: '' })
          }
          if (!formModel.addresses.some(a => a.type === 'PrimaryPractice')) {
            formModel.addresses.push({ type: 'PrimaryPractice', line1: '', city: '', state: '', zip: '' })
          }
          setModel(formModel)
          const initialAffOrgs = loadedAffiliations
            .filter(a => (a.organizationName && a.organizationName.trim()) || a.organizationId)
            .map(a => {
              const affOrgName = (a.organizationName || '').trim()
              const affOrgId = a.organizationId
              const affAddr = a.address
                ? typeof a.address === 'string'
                  ? a.address
                  : [a.address.line1, a.address.city, a.address.state, a.address.zip].filter(Boolean).join(', ')
                : ''
              const affLabel = [affOrgName, affAddr].filter(Boolean).join(' | ') || affOrgName
              return {
                id: affOrgId || undefined,
                label: affLabel,
                value: affOrgName,
                data: affOrgId ? { id: affOrgId, address: a.address } : undefined,
              }
            })
          setOrgOptions(initialAffOrgs)
          loadInitialOrgs(loadedAffiliations)
        })
        .catch(err => {
          // eslint-disable-next-line no-console
          console.warn(err)
          setError('Failed to load provider details')
        })
        .finally(() => setIsLoading(false))
    } else if (show) {
      const cleanInitial = { ...(initialData || {}) }
      const initVals = getInitialValues()

      const addresses =
        Array.isArray(cleanInitial.addresses) && cleanInitial.addresses.length > 0
          ? cleanInitial.addresses.map(a => ({ ...a }))
          : initVals.addresses

      const individuals =
        Array.isArray(cleanInitial.individuals) && cleanInitial.individuals.length > 0
          ? cleanInitial.individuals.map(i => ({ ...i }))
          : initVals.individuals

      const affiliations = Array.isArray(cleanInitial.affiliations)
        ? cleanInitial.affiliations.map(a => ({ ...a }))
        : initVals.affiliations

      const staffContacts = Array.isArray(cleanInitial.staffContacts)
        ? cleanInitial.staffContacts.map(c => ({
            ...c,
            phone: c.phone ? formatPhone(c.phone) : '',
          }))
        : initVals.staffContacts

      delete cleanInitial.addresses
      delete cleanInitial.individuals
      delete cleanInitial.affiliations
      delete cleanInitial.staffContacts

      const rawInitClass = String(cleanInitial.classification || initialClassification || '')
        .trim()
        .toLowerCase()
      const hasInitIndivName = Boolean(
        (cleanInitial.firstName && cleanInitial.firstName.trim()) ||
          (cleanInitial.lastName && cleanInitial.lastName.trim()),
      )
      const isIndiv =
        rawInitClass === 'individual' ||
        (rawInitClass !== 'organization' && (Boolean(cleanInitial.isIndividual) || hasInitIndivName))

      const initOrgName = !isIndiv
        ? cleanInitial.organizationName || cleanInitial.name || ''
        : cleanInitial.organizationName || ''

      setModel({
        ...initVals,
        ...cleanInitial,
        name: isIndiv ? cleanInitial.name || '' : initOrgName,
        organizationName: initOrgName,
        legacyDisplayName: '',
        type: cleanInitial.type || initialType || 'Treatment',
        classification: isIndiv
          ? 'Individual'
          : cleanInitial.classification || initialClassification || initVals.classification,
        phone: cleanInitial.phone ? formatPhone(cleanInitial.phone) : '',
        fax: cleanInitial.fax ? formatPhone(cleanInitial.fax) : '',
        addresses,
        individuals: individuals.map(i => ({
          ...i,
          phone: i.phone ? formatPhone(i.phone) : '',
        })),
        affiliations,
        staffContacts,
      })
      const initialAffOrgs = affiliations
        .filter(a => (a.organizationName && a.organizationName.trim()) || a.organizationId)
        .map(a => {
          const affOrgName = (a.organizationName || '').trim()
          const affOrgId = a.organizationId
          const affAddr = a.address
            ? typeof a.address === 'string'
              ? a.address
              : [a.address.line1, a.address.city, a.address.state, a.address.zip].filter(Boolean).join(', ')
            : ''
          const affLabel = [affOrgName, affAddr].filter(Boolean).join(' | ') || affOrgName
          return {
            id: affOrgId || undefined,
            label: affLabel,
            value: affOrgName,
            data: affOrgId ? { id: affOrgId, address: a.address } : undefined,
          }
        })
      setOrgOptions(initialAffOrgs)
      loadInitialOrgs(affiliations)
      setError(null)
      setIsLoading(false)
    } else {
      setModel(getInitialValues())
      setOrgOptions([])
      setError(null)
      setIsLoading(false)
    }
  }, [show, providerId, initialType, initialClassification, initialData, loadInitialOrgs])

  const handleSubmit = async (values, { setSubmitting }) => {
    setError(null)
    try {
      const submissionValues = {
        ...values,
        type: values.type || initialType || 'Treatment',
      }
      delete submissionValues.legacyDisplayName
      if (values.classification === 'Individual') {
        submissionValues.isSoleProprietor = false
        submissionValues.name = `${values.firstName || ''} ${values.lastName || ''}`.trim()
        if (submissionValues.affiliations && submissionValues.affiliations.length > 0) {
          const validAffs = submissionValues.affiliations.filter(
            a => (a.organizationName && a.organizationName.trim()) || a.organizationId,
          )
          submissionValues.affiliations = validAffs.map(a => ({
            ...a,
            organizationId: a.organizationId || null,
            organizationName: (a.organizationName || '').trim(),
            role: a.role || 'Primary Practice',
            isPrimary: !!a.isPrimary,
            address: a.address && typeof a.address === 'object' ? a.address : null,
          }))
          const primary = submissionValues.affiliations.find(a => a.isPrimary) || submissionValues.affiliations[0]
          if (primary) {
            submissionValues.organizationName = primary.organizationName || null
            submissionValues.parentProviderId = primary.organizationId || null
          } else {
            submissionValues.organizationName = null
            submissionValues.parentProviderId = null
          }
        } else {
          submissionValues.affiliations = []
          submissionValues.organizationName = null
          submissionValues.parentProviderId = null
        }
      } else {
        const cleanOrgName = (values.organizationName || values.name || '').trim()
        submissionValues.classification = 'Organization'
        submissionValues.organizationName = cleanOrgName
        submissionValues.name = cleanOrgName
        submissionValues.isSoleProprietor = false
        submissionValues.affiliations = []
        if (Array.isArray(submissionValues.individuals)) {
          submissionValues.individuals = submissionValues.individuals.filter(
            i =>
              i.id ||
              (i.firstName && i.firstName.trim()) ||
              (i.lastName && i.lastName.trim()) ||
              (i.npi && i.npi.trim()),
          )
        }
      }
      submissionValues.phone = values.phone ? formatPhone(values.phone) : null
      submissionValues.fax = values.fax ? formatPhone(values.fax) : null
      if (Array.isArray(submissionValues.individuals)) {
        submissionValues.individuals = submissionValues.individuals.map(i => ({
          ...i,
          phone: i.phone ? formatPhone(i.phone) : null,
        }))
      }
      if (Array.isArray(submissionValues.staffContacts)) {
        submissionValues.staffContacts = submissionValues.staffContacts
          .filter(
            c =>
              (c.name && c.name.trim()) ||
              (c.phone && c.phone.trim()) ||
              (c.email && c.email.trim()) ||
              (c.directAddress && c.directAddress.trim()),
          )
          .map(c => ({
            ...c,
            name: (c.name || '').trim() || 'Office Staff',
            role: (c.role || '').trim() || 'Office Staff',
            phone: c.phone ? formatPhone(c.phone) : null,
            extension: (c.extension || '').trim() || null,
            email: (c.email || '').trim() || null,
            directAddress: (c.directAddress || '').trim() || null,
            notes: (c.notes || '').trim() || null,
            isPrimary: !!c.isPrimary,
          }))
      }
      let result
      if (providerId) {
        result = await ProviderService.update(providerId, submissionValues)
      } else {
        result = await ProviderService.create(submissionValues)
      }
      if (onSuccess) onSuccess(result)
      onHide()
    } catch (err) {
      setError(err.message || 'An error occurred')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      {showConfirmClose && (
        <ConfirmationModal
          title="Discard Changes?"
          body="You have unsaved changes. Are you sure you want to close without saving?"
          confirmText="Discard Changes"
          cancelText="Keep Editing"
          style={{ zIndex: 1070 }}
          backdropClassName="nested-modal-backdrop"
          onConfirm={() => {
            setShowConfirmClose(false)
            onHide()
          }}
          onClose={() => setShowConfirmClose(false)}
        />
      )}
      <Modal
        show={show}
        onHide={handleClose}
        onEntered={() => firstInputRef.current?.focus()}
        enforceFocus={false}
        size="xl"
        className={className}
        dialogClassName={dialogClassName}
        backdropClassName={backdropClassName}
        style={style}
      >
        <Formik
          innerRef={formikRef}
          enableReinitialize
          initialValues={model}
          validationSchema={schema}
          onSubmit={handleSubmit}
        >
          {({ handleSubmit, handleChange, setFieldValue, values, touched, errors, isSubmitting, dirty }) => (
            <Form noValidate onSubmit={handleSubmit}>
              <Modal.Header closeButton className="align-items-center">
                <div className="d-flex align-items-center flex-wrap w-100 justify-content-between mr-3">
                  <Modal.Title className="d-flex align-items-center">
                    <span>
                      {providerId
                        ? `Edit ${getProviderTypeLabel(initialType || model.type)}`
                        : `New ${getProviderTypeLabel(initialType || model.type)}`}
                    </span>
                    {providerId && !isLoading && !error && (
                      <Badge
                        variant={
                          values.classification === 'Organization'
                            ? 'secondary'
                            : values.classification === 'SoleProprietor'
                            ? 'warning'
                            : 'info'
                        }
                        className="ml-2 font-weight-normal"
                        style={{ fontSize: '0.85rem' }}
                      >
                        {values.classification === 'Organization'
                          ? '🏢 Organization'
                          : values.classification === 'SoleProprietor'
                          ? '👤 Sole Proprietor'
                          : '👤 Individual'}
                      </Badge>
                    )}
                  </Modal.Title>
                  {!providerId && (
                    <div className="d-flex align-items-center mt-2 mt-sm-0">
                      <span className="text-muted small font-weight-bold mr-2">Entity Type:</span>
                      <ButtonGroup size="sm" aria-label="Provider Classification">
                        <Button
                          type="button"
                          variant={values.classification !== 'Organization' ? 'primary' : 'outline-secondary'}
                          onClick={() => setFieldValue('classification', 'Individual')}
                        >
                          <span role="img" aria-label="individual" className="mr-1">
                            👤
                          </span>
                          Individual
                        </Button>
                        <Button
                          type="button"
                          variant={values.classification === 'Organization' ? 'primary' : 'outline-secondary'}
                          onClick={() => setFieldValue('classification', 'Organization')}
                        >
                          <span role="img" aria-label="organization" className="mr-1">
                            🏢
                          </span>
                          Organization
                        </Button>
                      </ButtonGroup>
                    </div>
                  )}
                </div>
              </Modal.Header>
              <Modal.Body>
                {isLoading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                    <div className="mt-2 text-muted">Loading provider details...</div>
                  </div>
                ) : (
                  <>
                    {error && <div className="alert alert-danger">{error}</div>}
                    {reclassifiedAlert && (
                      <Alert variant="info" dismissible onClose={() => setReclassifiedAlert(null)}>
                        ℹ️ {reclassifiedAlert}
                      </Alert>
                    )}
                    {values.classification === 'SoleProprietor' && (
                      <Alert variant="warning" className="d-flex justify-content-between align-items-center flex-wrap">
                        <div className="mr-3">
                          <strong>Sole Proprietor Record:</strong> This record is currently categorized as a{' '}
                          <strong>Sole Proprietor</strong> and appears under Independent Practitioners. Convert it to an{' '}
                          <strong>Organization / Facility</strong> or an <strong>Individual Practitioner</strong>:
                        </div>
                        <div className="mt-2 mt-md-0 d-flex" style={{ gap: '8px' }}>
                          <Button
                            type="button"
                            variant="primary"
                            size="sm"
                            onClick={() => {
                              const orgNameVal = (values.organizationName || values.name || '').trim()
                              setFieldValue('classification', 'Organization')
                              setFieldValue('isSoleProprietor', false)
                              setFieldValue('organizationName', orgNameVal)
                              setFieldValue('name', orgNameVal)
                              setFieldValue('firstName', '')
                              setFieldValue('lastName', '')
                              setFieldValue('suffix', '')
                              setFieldValue('affiliations', [])
                              setReclassifiedAlert(
                                'Provider reclassified as Organization. Review the updated fields below and click "Save Changes" to save.',
                              )
                            }}
                          >
                            🏢 Convert to Organization
                          </Button>
                          <Button
                            type="button"
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => setShowReclassifyModal(true)}
                          >
                            👤 Reclassify...
                          </Button>
                        </div>
                      </Alert>
                    )}
                    {values.classification !== 'Individual' && values.legacyDisplayName && (
                      <Alert variant="warning" className="d-flex justify-content-between align-items-center flex-wrap">
                        <div className="mr-3">
                          <strong>Legacy Combined Record:</strong> This organization record (
                          <strong>{values.organizationName}</strong>) also had practitioner name{' '}
                          <strong>&ldquo;{values.legacyDisplayName}&rdquo;</strong> stored in its legacy display name
                          field.
                        </div>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="mt-2 mt-md-0"
                          onClick={() => setShowReclassifyModal(true)}
                        >
                          Split &ldquo;{values.legacyDisplayName}&rdquo; into Individual Practitioner
                        </Button>
                      </Alert>
                    )}

                    {values.classification === 'Individual' ? (
                      <Row>
                        <Col md={3}>
                          <Form.Group controlId="firstName">
                            <Form.Label>First Name</Form.Label>
                            <Form.Control
                              ref={firstInputRef}
                              type="text"
                              name="firstName"
                              value={values.firstName || ''}
                              onChange={handleChange}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group controlId="lastName">
                            <Form.Label>Last Name</Form.Label>
                            <Form.Control
                              type="text"
                              name="lastName"
                              value={values.lastName || ''}
                              onChange={handleChange}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={2}>
                          <Form.Group controlId="suffix">
                            <Form.Label>Suffix</Form.Label>
                            <Form.Control
                              type="text"
                              name="suffix"
                              value={values.suffix || ''}
                              onChange={handleChange}
                              placeholder="e.g. PA-C"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group controlId="specialties">
                            <Form.Label>Specialties</Form.Label>
                            <Form.Control
                              type="text"
                              name="specialties"
                              value={values.specialties || ''}
                              onChange={handleChange}
                              placeholder="e.g. Occupational Medicine"
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    ) : (
                      <Row>
                        <Col md={5}>
                          <Form.Group controlId="organizationName">
                            <Form.Label>Organization Name (Required)</Form.Label>
                            <Form.Control
                              ref={firstInputRef}
                              type="text"
                              name="organizationName"
                              value={values.organizationName || ''}
                              onChange={e => {
                                handleChange(e)
                                setFieldValue('name', e.target.value)
                              }}
                              isInvalid={touched.organizationName && !!errors.organizationName}
                            />
                            <Form.Control.Feedback type="invalid">{errors.organizationName}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group controlId="specialties">
                            <Form.Label>Specialties / Facility Type</Form.Label>
                            <Form.Control
                              type="text"
                              name="specialties"
                              value={values.specialties || ''}
                              onChange={handleChange}
                              placeholder="e.g. Orthopedics, Physical Therapy, Imaging"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group controlId="npi">
                            <Form.Label>Organization NPI</Form.Label>
                            <Form.Control
                              type="text"
                              name="npi"
                              value={values.npi || ''}
                              onChange={handleChange}
                              maxLength={10}
                              placeholder="10-digit NPI"
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    )}

                    {values.classification === 'Individual' && (
                      <>
                        <Row className="mt-3">
                          <Col md={4}>
                            <Form.Group controlId="npi">
                              <Form.Label>Individual NPI</Form.Label>
                              <Form.Control
                                type="text"
                                name="npi"
                                value={values.npi || ''}
                                onChange={handleChange}
                                maxLength={10}
                                placeholder="10-digit NPI"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={8}>
                            <Form.Group controlId="primaryFacilityQuickLink">
                              <div className="d-flex justify-content-between align-items-center">
                                <Form.Label className="mb-1">
                                  Primary Facility / Practice (Directory Autocomplete)
                                </Form.Label>
                                <span className="text-muted small">Syncs with Primary Practice affiliation</span>
                              </div>
                              {(() => {
                                const primaryIdx = (values.affiliations || []).findIndex(a => a.isPrimary)
                                const effectiveIdx =
                                  primaryIdx >= 0 ? primaryIdx : (values.affiliations || []).length > 0 ? 0 : -1
                                const primaryAff = effectiveIdx >= 0 ? values.affiliations[effectiveIdx] : null
                                const currentOrgName = (
                                  primaryAff?.organizationName ||
                                  values.organizationName ||
                                  ''
                                ).trim()
                                const currentOrgId = primaryAff?.organizationId || values.parentProviderId || null

                                const matchedOpt =
                                  (currentOrgId || currentOrgName) &&
                                  orgOptions.find(o => {
                                    if (
                                      currentOrgId &&
                                      o.id &&
                                      String(o.id).toLowerCase() === String(currentOrgId).toLowerCase()
                                    ) {
                                      return true
                                    }
                                    if (
                                      currentOrgName &&
                                      o.value &&
                                      o.value.trim().toLowerCase() === currentOrgName.toLowerCase()
                                    ) {
                                      return true
                                    }
                                    return false
                                  })

                                const fallbackPrimaryOpt =
                                  currentOrgName || currentOrgId
                                    ? {
                                        id: currentOrgId || undefined,
                                        label: currentOrgName,
                                        value: currentOrgName,
                                      }
                                    : null

                                const selectedPrimaryValue = matchedOpt || fallbackPrimaryOpt
                                const allPrimaryOptions =
                                  matchedOpt || !fallbackPrimaryOpt ? orgOptions : [...orgOptions, fallbackPrimaryOpt]

                                const syncPrimaryAffiliation = (orgName, orgId, orgData) => {
                                  setFieldValue('organizationName', orgName || '')
                                  setFieldValue('parentProviderId', orgId || null)

                                  const currentAffs = Array.isArray(values.affiliations) ? [...values.affiliations] : []
                                  if (!orgName && !orgId) {
                                    if (effectiveIdx >= 0) {
                                      currentAffs.splice(effectiveIdx, 1)
                                      setFieldValue('affiliations', currentAffs)
                                    }
                                    return
                                  }

                                  const newAffEntry = {
                                    ...(effectiveIdx >= 0 ? currentAffs[effectiveIdx] : {}),
                                    organizationId: orgId || null,
                                    organizationName: orgName,
                                    role: (effectiveIdx >= 0 && currentAffs[effectiveIdx]?.role) || 'Primary Practice',
                                    isPrimary: true,
                                    address:
                                      orgData?.address ||
                                      (effectiveIdx >= 0 ? currentAffs[effectiveIdx]?.address : null),
                                  }

                                  if (effectiveIdx >= 0) {
                                    currentAffs[effectiveIdx] = newAffEntry
                                  } else {
                                    currentAffs.unshift(newAffEntry)
                                  }
                                  setFieldValue(
                                    'affiliations',
                                    currentAffs.map((a, i) => ({
                                      ...a,
                                      isPrimary: effectiveIdx >= 0 ? i === effectiveIdx : i === 0,
                                    })),
                                  )
                                }

                                return (
                                  <div className="primary-facility-autocomplete">
                                    <CreatableSelect
                                      classNamePrefix="react-select"
                                      options={allPrimaryOptions}
                                      value={selectedPrimaryValue}
                                      components={{ Option: CustomOption, MenuList: CustomMenuList }}
                                      onInputChange={handleOrgInputChange}
                                      onChange={option => {
                                        if (option) {
                                          const rawVal = option.value || option.label || ''
                                          const orgName = rawVal.includes(' | ')
                                            ? rawVal.split(' | ')[0].trim()
                                            : rawVal.trim()
                                          const orgId = option.id || option.data?.id || null
                                          setOrgOptions(prev => {
                                            const exists = prev.some(
                                              o =>
                                                (orgId &&
                                                  o.id &&
                                                  String(o.id).toLowerCase() === String(orgId).toLowerCase()) ||
                                                (orgName && o.value && o.value.toLowerCase() === orgName.toLowerCase()),
                                            )
                                            return exists ? prev : [option, ...prev]
                                          })
                                          syncPrimaryAffiliation(orgName, orgId, option.data)
                                        } else {
                                          syncPrimaryAffiliation('', null, null)
                                        }
                                      }}
                                      onCreateOption={inputValue => {
                                        const orgName = (inputValue || '').trim()
                                        if (!orgName) return
                                        const newOpt = {
                                          id: null,
                                          label: orgName,
                                          value: orgName,
                                          data: undefined,
                                        }
                                        setOrgOptions(prev => [newOpt, ...prev])
                                        syncPrimaryAffiliation(orgName, null, null)
                                      }}
                                      placeholder="Search directory for clinic, hospital, or group practice..."
                                      isClearable
                                      closeMenuOnSelect={true}
                                      tabSelectsValue={false}
                                      menuPortalTarget={document.body}
                                      styles={{
                                        control: provided => ({
                                          ...provided,
                                          borderColor: '#ced4da',
                                          minHeight: '38px',
                                          fontSize: '0.9rem',
                                        }),
                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                      }}
                                    />
                                  </div>
                                )
                              })()}
                            </Form.Group>
                          </Col>
                        </Row>

                        <hr />
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h5 className="mb-0">Affiliated Organizations / Facilities</h5>
                          <span className="text-muted small">Practicing locations and hospital privileges</span>
                        </div>

                        <FieldArray name="affiliations">
                          {({ push, remove }) => (
                            <div>
                              {(!values.affiliations || values.affiliations.length === 0) && (
                                <p className="text-muted font-italic mb-2">
                                  No affiliated organizations. Add an organization to link this clinician to a practice,
                                  hospital, or imaging center.
                                </p>
                              )}
                              {(values.affiliations || []).map((aff, index) => {
                                const affOrgName = (aff.organizationName || '').trim()
                                const affOrgId = aff.organizationId
                                const affAddr = aff.address
                                  ? typeof aff.address === 'string'
                                    ? aff.address
                                    : [aff.address.line1, aff.address.city, aff.address.state, aff.address.zip]
                                        .filter(Boolean)
                                        .join(', ')
                                  : ''
                                const affLabel = [affOrgName, affAddr].filter(Boolean).join(' | ') || affOrgName

                                const matchedOrg =
                                  (affOrgId || affOrgName) &&
                                  orgOptions.find(o => {
                                    if (
                                      affOrgId &&
                                      o.id &&
                                      String(o.id).toLowerCase() === String(affOrgId).toLowerCase()
                                    ) {
                                      return true
                                    }
                                    if (
                                      affOrgName &&
                                      o.value &&
                                      o.value.trim().toLowerCase() === affOrgName.toLowerCase()
                                    ) {
                                      return true
                                    }
                                    return false
                                  })

                                const fallbackOpt =
                                  affOrgName || affOrgId
                                    ? {
                                        id: affOrgId || undefined,
                                        label: affLabel,
                                        value: affOrgName,
                                        data: affOrgId ? { id: affOrgId, address: aff.address } : undefined,
                                      }
                                    : null

                                const selectValue = matchedOrg || fallbackOpt
                                const selectOptions =
                                  matchedOrg || !fallbackOpt ? orgOptions : [...orgOptions, fallbackOpt]

                                return (
                                  <div
                                    key={index}
                                    className={`mb-3 p-3 border rounded ${
                                      aff.isPrimary ? 'bg-light border-primary' : ''
                                    }`}
                                  >
                                    <Row className="align-items-center">
                                      <Col md={5}>
                                        <Form.Group className="mb-0">
                                          <Form.Label className="small font-weight-bold">
                                            Organization / Facility Name
                                          </Form.Label>
                                          <CreatableSelect
                                            classNamePrefix="react-select"
                                            options={selectOptions}
                                            value={selectValue}
                                            components={{ Option: CustomOption, MenuList: CustomMenuList }}
                                            onInputChange={handleOrgInputChange}
                                            getOptionValue={opt => {
                                              const id = opt?.id || opt?.data?.id
                                              if (id) return String(id).toLowerCase()
                                              return (opt?.value || opt?.organization || opt?.name || opt?.label || '')
                                                .trim()
                                                .toLowerCase()
                                            }}
                                            getOptionLabel={opt =>
                                              opt?.label || opt?.value || opt?.organization || opt?.name || ''
                                            }
                                            isOptionSelected={(option, valArray) =>
                                              valArray.some(sv => {
                                                const optId = option?.id || option?.data?.id
                                                const svId = sv?.id || sv?.data?.id
                                                if (
                                                  optId &&
                                                  svId &&
                                                  String(optId).toLowerCase() === String(svId).toLowerCase()
                                                ) {
                                                  return true
                                                }
                                                const optVal = (option?.value || option?.label || '')
                                                  .trim()
                                                  .toLowerCase()
                                                const svVal = (sv?.value || sv?.label || '').trim().toLowerCase()
                                                return optVal && svVal && optVal === svVal
                                              })
                                            }
                                            onChange={option => {
                                              if (option) {
                                                const rawVal = option.value || option.label || ''
                                                const orgName = rawVal.includes(' | ')
                                                  ? rawVal.split(' | ')[0].trim()
                                                  : rawVal.trim()
                                                const orgId = option.id || option.data?.id || null
                                                setOrgOptions(prev => {
                                                  const exists = prev.some(
                                                    o =>
                                                      (orgId &&
                                                        o.id &&
                                                        String(o.id).toLowerCase() === String(orgId).toLowerCase()) ||
                                                      (orgName &&
                                                        o.value &&
                                                        o.value.toLowerCase() === orgName.toLowerCase()),
                                                  )
                                                  return exists ? prev : [option, ...prev]
                                                })
                                                setFieldValue(`affiliations[${index}].organizationName`, orgName)
                                                setFieldValue(`affiliations[${index}].organizationId`, orgId)
                                                if (option.data?.address) {
                                                  setFieldValue(`affiliations[${index}].address`, option.data.address)
                                                }
                                                if (aff.isPrimary) {
                                                  setFieldValue('organizationName', orgName)
                                                  setFieldValue('parentProviderId', orgId)
                                                }
                                              } else {
                                                setFieldValue(`affiliations[${index}].organizationName`, '')
                                                setFieldValue(`affiliations[${index}].organizationId`, null)
                                                setFieldValue(`affiliations[${index}].address`, null)
                                                if (aff.isPrimary) {
                                                  setFieldValue('organizationName', '')
                                                  setFieldValue('parentProviderId', null)
                                                }
                                              }
                                            }}
                                            onCreateOption={inputValue => {
                                              const orgName = (inputValue || '').trim()
                                              const newOpt = {
                                                id: null,
                                                label: orgName,
                                                value: orgName,
                                                data: undefined,
                                              }
                                              setOrgOptions(prev => [newOpt, ...prev])
                                              setFieldValue(`affiliations[${index}].organizationName`, orgName)
                                              setFieldValue(`affiliations[${index}].organizationId`, null)
                                              setFieldValue(`affiliations[${index}].address`, null)
                                              if (aff.isPrimary) {
                                                setFieldValue('organizationName', orgName)
                                                setFieldValue('parentProviderId', null)
                                              }
                                            }}
                                            placeholder="Type to search or enter organization..."
                                            isClearable
                                            closeMenuOnSelect={true}
                                            tabSelectsValue={false}
                                            menuPortalTarget={document.body}
                                            styles={{
                                              control: provided => ({
                                                ...provided,
                                                borderColor: '#ced4da',
                                                minHeight: '38px',
                                                fontSize: '0.9rem',
                                              }),
                                              menuPortal: base => ({ ...base, zIndex: 9999 }),
                                              option: (provided, state) => ({
                                                ...provided,
                                                backgroundColor: state.isSelected
                                                  ? '#007bff'
                                                  : state.isFocused
                                                  ? '#e9ecef'
                                                  : 'transparent',
                                                color: state.isSelected ? '#ffffff' : '#212529',
                                                cursor: 'pointer',
                                              }),
                                            }}
                                          />
                                        </Form.Group>
                                      </Col>
                                      <Col md={4}>
                                        <Form.Group className="mb-0">
                                          <Form.Label className="small font-weight-bold">Role / Privilege</Form.Label>
                                          <Form.Control
                                            type="text"
                                            name={`affiliations[${index}].role`}
                                            value={aff.role || ''}
                                            onChange={handleChange}
                                            placeholder="e.g. Primary Practice, Attending, Visiting"
                                          />
                                        </Form.Group>
                                      </Col>
                                      <Col md={3} className="d-flex flex-column justify-content-center pt-2">
                                        <Form.Check
                                          type="radio"
                                          id={`aff-primary-${index}`}
                                          name="primaryAffiliationRadio"
                                          label={
                                            <span className={aff.isPrimary ? 'font-weight-bold text-primary' : ''}>
                                              Primary Practice
                                            </span>
                                          }
                                          checked={!!aff.isPrimary}
                                          onChange={() => {
                                            ;(values.affiliations || []).forEach((_, i) => {
                                              setFieldValue(`affiliations[${i}].isPrimary`, i === index)
                                            })
                                            setFieldValue('organizationName', aff.organizationName || '')
                                            if (aff.organizationId) {
                                              setFieldValue('parentProviderId', aff.organizationId)
                                            }
                                          }}
                                        />
                                        <div className="mt-1">
                                          <Button
                                            variant="link"
                                            size="sm"
                                            className="text-danger p-0"
                                            onClick={() => {
                                              remove(index)
                                              const remainingAffs = values.affiliations || []
                                              if (aff.isPrimary && remainingAffs.length > 1) {
                                                const nextIndex = index === 0 ? 1 : 0
                                                setFieldValue(`affiliations[${nextIndex}].isPrimary`, true)
                                                setFieldValue(
                                                  'organizationName',
                                                  remainingAffs[nextIndex]?.organizationName || '',
                                                )
                                              } else if (remainingAffs.length <= 1) {
                                                setFieldValue('organizationName', '')
                                              }
                                            }}
                                          >
                                            Remove
                                          </Button>
                                        </div>
                                      </Col>
                                    </Row>
                                  </div>
                                )
                              })}
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => {
                                  const isFirst = !values.affiliations || values.affiliations.length === 0
                                  push({
                                    organizationId: null,
                                    organizationName: '',
                                    role: isFirst ? 'Primary Practice' : 'Attending',
                                    isPrimary: isFirst,
                                  })
                                }}
                              >
                                + Add Affiliated Organization
                              </Button>
                            </div>
                          )}
                        </FieldArray>
                      </>
                    )}
                    <hr />
                    <h5>Contact Information</h5>
                    <Row>
                      <Col md={3}>
                        <Form.Group controlId="email">
                          <Form.Label>Main Email</Form.Label>
                          <Form.Control type="email" name="email" value={values.email} onChange={handleChange} />
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group controlId="phone">
                          <Form.Label>Main Phone</Form.Label>
                          <InputMask
                            mask="(999) 999-9999"
                            className="form-control"
                            type="tel"
                            name="phone"
                            value={values.phone}
                            onChange={handleChange}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group controlId="fax">
                          <Form.Label>Fax</Form.Label>
                          <InputMask
                            mask="(999) 999-9999"
                            className="form-control"
                            type="tel"
                            name="fax"
                            value={values.fax}
                            onChange={handleChange}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group controlId="website">
                          <Form.Label>Website</Form.Label>
                          <Form.Control type="text" name="website" value={values.website} onChange={handleChange} />
                        </Form.Group>
                      </Col>
                    </Row>

                    <hr />
                    <h5>Addresses</h5>
                    <FieldArray name="addresses">
                      {({ push, remove }) => (
                        <>
                          {(values.addresses || []).map((addr, index) => (
                            <div key={index} className="mb-3 p-3 border rounded">
                              <Row>
                                <Col md={3}>
                                  <Form.Group>
                                    <Form.Label>Type</Form.Label>
                                    <Form.Control
                                      type="text"
                                      name={`addresses[${index}].type`}
                                      value={addr.type}
                                      onChange={handleChange}
                                      readOnly={index < 2}
                                    />
                                  </Form.Group>
                                </Col>
                                <Col md={9}>
                                  <Row>
                                    <Col md={12}>
                                      <Form.Group>
                                        <Form.Label>Address</Form.Label>
                                        <SmartAddressInput
                                          value={{
                                            street: addr.line1,
                                            city: addr.city,
                                            state: addr.state,
                                            zip: addr.zip,
                                          }}
                                          onChange={newAddr => {
                                            setFieldValue(`addresses[${index}].line1`, newAddr.street)
                                            setFieldValue(`addresses[${index}].city`, newAddr.city)
                                            setFieldValue(`addresses[${index}].state`, newAddr.state)
                                            setFieldValue(`addresses[${index}].zip`, newAddr.zip)
                                          }}
                                        />
                                      </Form.Group>
                                    </Col>
                                  </Row>
                                </Col>
                              </Row>
                              {index >= 2 && (
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => remove(index)}
                                  className="mt-2"
                                >
                                  Remove Address
                                </Button>
                              )}
                            </div>
                          ))}
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => push({ type: 'SecondaryPractice', line1: '', city: '', state: '', zip: '' })}
                          >
                            + Add Secondary Address
                          </Button>
                        </>
                      )}
                    </FieldArray>

                    {values.classification !== 'Individual' && (
                      <>
                        <hr />
                        <h5>Individuals</h5>
                        <FieldArray name="individuals">
                          {({ push, remove }) => (
                            <>
                              {(values.individuals || []).map((indiv, index) => (
                                <div key={index} className="mb-3 p-3 border rounded bg-light">
                                  <Row>
                                    <Col md={2}>
                                      <Form.Group>
                                        <Form.Label>Title / Suffix</Form.Label>
                                        <Form.Control
                                          name={`individuals[${index}].title`}
                                          value={indiv.title || ''}
                                          onChange={handleChange}
                                          placeholder="e.g. MD, PA-C"
                                        />
                                      </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                      <Form.Group>
                                        <Form.Label>First Name</Form.Label>
                                        <Form.Control
                                          name={`individuals[${index}].firstName`}
                                          value={indiv.firstName || ''}
                                          onChange={handleChange}
                                        />
                                      </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                      <Form.Group>
                                        <Form.Label>Last Name</Form.Label>
                                        <Form.Control
                                          name={`individuals[${index}].lastName`}
                                          value={indiv.lastName || ''}
                                          onChange={handleChange}
                                        />
                                      </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                      <Form.Group>
                                        <Form.Label>Individual NPI</Form.Label>
                                        <Form.Control
                                          name={`individuals[${index}].npi`}
                                          value={indiv.npi || ''}
                                          onChange={handleChange}
                                          maxLength={10}
                                        />
                                      </Form.Group>
                                    </Col>
                                  </Row>
                                  <Row>
                                    <Col md={6}>
                                      <Form.Group>
                                        <Form.Label>Email</Form.Label>
                                        <Form.Control
                                          name={`individuals[${index}].email`}
                                          value={indiv.email || ''}
                                          onChange={handleChange}
                                        />
                                      </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                      <Form.Group>
                                        <Form.Label>Phone</Form.Label>
                                        <InputMask
                                          mask="(999) 999-9999"
                                          className="form-control"
                                          type="tel"
                                          name={`individuals[${index}].phone`}
                                          value={indiv.phone || ''}
                                          onChange={handleChange}
                                        />
                                      </Form.Group>
                                    </Col>
                                  </Row>
                                  {!values.isSoleProprietor && (values.individuals || []).length > 1 && (
                                    <Button variant="outline-danger" size="sm" onClick={() => remove(index)}>
                                      Remove Individual
                                    </Button>
                                  )}
                                </div>
                              ))}
                              {!values.isSoleProprietor && (
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() =>
                                    push({ firstName: '', lastName: '', title: '', email: '', phone: '', npi: '' })
                                  }
                                >
                                  + Add Individual
                                </Button>
                              )}
                            </>
                          )}
                        </FieldArray>
                      </>
                    )}

                    <hr className="mt-4" />
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div>
                        <h6 className="mb-0">Staff &amp; Office Contacts</h6>
                        <small className="text-muted">
                          Medical assistants, referral coordinators, records clerks, and billing contacts
                        </small>
                      </div>
                    </div>
                    <FieldArray name="staffContacts">
                      {({ push, remove }) => (
                        <>
                          {(values.staffContacts || []).map((contact, index) => (
                            <div key={index} className="border p-3 mb-2 rounded bg-light">
                              <Row>
                                <Col md={5}>
                                  <Form.Group className="mb-2">
                                    <Form.Label className="small mb-1">Contact Name</Form.Label>
                                    <Form.Control
                                      size="sm"
                                      name={`staffContacts[${index}].name`}
                                      value={contact.name || ''}
                                      onChange={handleChange}
                                      placeholder="e.g. Sarah Jenkins"
                                    />
                                  </Form.Group>
                                </Col>
                                <Col md={4}>
                                  <Form.Group className="mb-2">
                                    <Form.Label className="small mb-1">Role / Department</Form.Label>
                                    <Form.Control
                                      size="sm"
                                      as="select"
                                      name={`staffContacts[${index}].role`}
                                      value={contact.role || 'Medical Assistant'}
                                      onChange={handleChange}
                                    >
                                      <option value="Medical Assistant">Medical Assistant (MA)</option>
                                      <option value="Referral Coordinator">Referral / Auth Coordinator</option>
                                      <option value="Scheduler">Scheduler</option>
                                      <option value="Records Clerk">Medical Records</option>
                                      <option value="Office Manager">Office Manager</option>
                                      <option value="Billing">Billing / Workers Comp</option>
                                      <option value="Case Coordinator">Nurse / Case Coordinator</option>
                                      <option value="Other">Other Staff</option>
                                    </Form.Control>
                                  </Form.Group>
                                </Col>
                                <Col md={3} className="d-flex align-items-center pt-3">
                                  <Form.Check
                                    type="checkbox"
                                    id={`staff-primary-${index}`}
                                    label="Primary Contact"
                                    checked={Boolean(contact.isPrimary)}
                                    onChange={e => setFieldValue(`staffContacts[${index}].isPrimary`, e.target.checked)}
                                    className="small"
                                  />
                                </Col>
                              </Row>
                              <Row>
                                <Col md={4}>
                                  <Form.Group className="mb-2">
                                    <Form.Label className="small mb-1">Direct Phone</Form.Label>
                                    <InputMask
                                      mask="(999) 999-9999"
                                      className="form-control form-control-sm"
                                      type="tel"
                                      name={`staffContacts[${index}].phone`}
                                      value={contact.phone || ''}
                                      onChange={handleChange}
                                      placeholder="(555) 123-4567"
                                    />
                                  </Form.Group>
                                </Col>
                                <Col md={2}>
                                  <Form.Group className="mb-2">
                                    <Form.Label className="small mb-1">Ext.</Form.Label>
                                    <Form.Control
                                      size="sm"
                                      name={`staffContacts[${index}].extension`}
                                      value={contact.extension || ''}
                                      onChange={handleChange}
                                      placeholder="e.g. 204"
                                    />
                                  </Form.Group>
                                </Col>
                                <Col md={6}>
                                  <Form.Group className="mb-2">
                                    <Form.Label className="small mb-1">Email</Form.Label>
                                    <Form.Control
                                      size="sm"
                                      type="email"
                                      name={`staffContacts[${index}].email`}
                                      value={contact.email || ''}
                                      onChange={handleChange}
                                      placeholder="staff@clinic.org"
                                    />
                                  </Form.Group>
                                </Col>
                              </Row>
                              <Row className="align-items-end">
                                <Col md={5}>
                                  <Form.Group className="mb-0">
                                    <Form.Label className="small mb-1">
                                      Direct Address (EHR Secure Messaging)
                                    </Form.Label>
                                    <Form.Control
                                      size="sm"
                                      name={`staffContacts[${index}].directAddress`}
                                      value={contact.directAddress || ''}
                                      onChange={handleChange}
                                      placeholder="staff@direct.clinic.org"
                                    />
                                  </Form.Group>
                                </Col>
                                <Col md={5}>
                                  <Form.Group className="mb-0">
                                    <Form.Label className="small mb-1">Notes</Form.Label>
                                    <Form.Control
                                      size="sm"
                                      name={`staffContacts[${index}].notes`}
                                      value={contact.notes || ''}
                                      onChange={handleChange}
                                      placeholder="Best hours, fax attention, etc."
                                    />
                                  </Form.Group>
                                </Col>
                                <Col md={2} className="text-right">
                                  <Button variant="outline-danger" size="sm" onClick={() => remove(index)}>
                                    Remove
                                  </Button>
                                </Col>
                              </Row>
                            </div>
                          ))}
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() =>
                              push({
                                name: '',
                                role: 'Medical Assistant',
                                phone: '',
                                extension: '',
                                email: '',
                                directAddress: '',
                                notes: '',
                                isPrimary: false,
                              })
                            }
                          >
                            + Add Staff Contact
                          </Button>
                        </>
                      )}
                    </FieldArray>

                    <Form.Group controlId="notes" className="mt-3">
                      <Form.Label>Notes</Form.Label>
                      <Form.Control as="textarea" rows={3} name="notes" value={values.notes} onChange={handleChange} />
                    </Form.Group>
                  </>
                )}
              </Modal.Body>
              <Modal.Footer className="d-flex justify-content-between align-items-center">
                <div>
                  {providerId && (
                    <Button
                      variant="link"
                      className="p-0 text-muted small"
                      style={{ textDecoration: 'underline' }}
                      onClick={() => setShowReclassifyModal(true)}
                    >
                      {values.classification === 'Individual' || values.classification === 'SoleProprietor'
                        ? '🛠️ Need to reclassify this record? Convert to Organization / Facility...'
                        : '🛠️ Need to reclassify this record? Convert to Individual Practitioner...'}
                    </Button>
                  )}
                </div>
                <div>
                  <Button variant="secondary" onClick={handleClose} className="mr-2">
                    Close
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={isSubmitting || isLoading || (!dirty && !values.legacyDisplayName && !reclassifiedAlert)}
                  >
                    {isSubmitting ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>
      {showReclassifyModal && (
        <ReclassifyProviderModal
          show={showReclassifyModal}
          onHide={() => setShowReclassifyModal(false)}
          provider={{
            ...formikRef.current?.values,
            id: providerId,
          }}
          orgOptions={orgOptions}
          onConfirm={handleReclassifyConfirm}
        />
      )}
    </>
  )
}
